/**
 * The ExteLet's own protection policy — the step's copy and its naming rule.
 *
 * <h3>Why this object has to exist on the agent</h3>
 *
 * <p>An ExteLet's protection is materialized onto it from the limits the
 * tenant sets, and the backend attributes every rule it produces to the
 * agent's one `policy_skill` — `ExternalAgentPolicyBridge.resolveSolePolicySkillId:253`
 * looks for exactly one and returns null otherwise. Protect never created one,
 * so that returned null for every agent built here, and the consequence is at
 * `:308-320`: the reconcile pass that retires rules the tenant has stopped
 * asking for is skipped entirely when there is no owner. A limit removed in
 * the wizard kept enforcing, with nothing in the UI able to clear it.</p>
 *
 * <p>It is also what the Decide step reviews. `SkillReviewService:50` refuses
 * to review a skill still in the build phase, and
 * `PhaseGateService.promoteEvalReadyBuildSkills:348` only lifts a skill to
 * decide once it has a completed eval at or above the bar — its own javadoc
 * names this case: "Without this, an ExteLet's policy_skill stays in 'build'
 * forever." So this step comes before the measuring step, not after it.</p>
 */

/** Falls back rather than naming an agent it does not know. */
const UNNAMED = 'This agent';

/**
 * The name offered for a new protection policy, derived from the agent so a
 * tenant with several can tell them apart in the Policies list. Offered, not
 * imposed — the step prefills it and the tenant may write their own.
 */
export function defaultPolicySkillName(agentName) {
  const base = typeof agentName === 'string' ? agentName.trim() : '';
  return `${base || UNNAMED} — protection policy`;
}

/** The description stored with it, so the Policies list says what it is for. */
export function defaultPolicySkillDescription(agentName) {
  const base = typeof agentName === 'string' ? agentName.trim() : '';
  return `The protection policy for ${base || 'this agent'}. The limits set while `
    + `protecting it become enforceable rules attributed here.`;
}

/** Copy for the step. Kept beside the rule above rather than in the component. */
export const POLICY_SKILL_COPY = Object.freeze({
  nameLabel: 'Name',
  namePlaceholder: 'Protection policy',
  createLabel: 'Create its protection policy',
  creating: 'Creating…',
  existsTitle: 'Protection policy on file',
  existsBody: 'The limits you set next become enforceable rules attributed to it, '
    + 'and it is what you review before this agent may act.',
  emptyBody: 'This agent has nothing to attribute its protection to yet. Create its '
    + 'policy and the limits you set next attach to it — which is also what lets you '
    + 'change or remove them later.',
  nameRequired: 'Give it a name.',
});
